import {
  AnalyticsActionShareEvent,
  AnalyticsEventParam,
} from '@accedo/one-plugin-sdk';
import { Ref, useImperativeHandle, useState } from 'react';
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  TwitterShareButton,
} from 'react-share';
import styled from 'styled-components';

import { Item } from '#/client/models';
import { tb } from '#/store/translation/translation.store';

import { focusShadow } from '../styles';
import { Modal } from './Modal';

export interface SharingModalApi {
  open: (item?: Item) => void;
  close: () => void;
}
interface SharingModalProps {
  apiRef?: Ref<SharingModalApi>;
}

// Twitter logo in package has not (possibly will not) be updated to X logo, so we need to put our own.
// Unfortunate solution to react-share being out-dated and no future update in sight.
// https://github.com/nygardk/react-share/pull/504
const LocalTwitterXIcon = () => (
  <svg height="64" viewBox="0 0 64 64" width="64">
    <circle cx={32} cy={32} fill="#0B2972" r={31} />
    <path
      d="M 41.116 18.375 h 4.962 l -10.8405 12.39 l 12.753 16.86 H 38.005 l -7.821 -10.2255 L 21.235 47.625 H 16.27 l 11.595 -13.2525 L 15.631 18.375 H 25.87 l 7.0695 9.3465 z m -1.7415 26.28 h 2.7495 L 24.376 21.189 H 21.4255 z"
      fill="white"
    ></path>
  </svg>
);

/** Returns the share URL, defaulting to the current page URL if none is provided. */
export const getShareUrl = (url?: string) => {
  return url || window.location.href;
};

const IconRow = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin-bottom: 1rem;

  button:focus-visible {
    ${focusShadow}
  }

  button:focus:not(:focus-visible) {
    outline: none;
  }
`;

export const SharingModal = ({ apiRef }: SharingModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [item, setItem] = useState<Item>();

  const api = {
    open: (item?: Item) => {
      setItem(item);
      setIsOpen(true);
    },
    close: () => {
      // #ExternalAnalytics - Share
      // sending it on close is "best effort". we can't detect when it was actually shared, as it happens in iframes.
      document.dispatchEvent(
        new AnalyticsActionShareEvent({
          [AnalyticsEventParam.ContentId]: item?.id,
          [AnalyticsEventParam.Genre]: item?.genres,
          [AnalyticsEventParam.Title]: item?.title,
        })
      );

      setIsOpen(false);
    },
  };

  // Unlike the regular Modal, this one uses an imperative handle.
  // That means the PageController don't have to manage the modal open state, so the Page won't rerender every time.
  useImperativeHandle(apiRef, () => api);

  const buttonProps = {
    url: getShareUrl(item?.refs.share),
    onShareWindowClose: api.close,
  };

  const iconProps = {
    round: true,
  };

  return (
    <Modal
      closeOnEsc
      closeOnOverlayClick
      isOpen={isOpen}
      onClose={api.close}
      showCloseButton
    >
      <Modal.Title>{tb('button.share')}</Modal.Title>
      <IconRow>
        <FacebookShareButton {...buttonProps}>
          <FacebookIcon {...iconProps} />
        </FacebookShareButton>
        <TwitterShareButton {...buttonProps}>
          <LocalTwitterXIcon />
        </TwitterShareButton>
        <EmailShareButton {...buttonProps} subject={item?.title}>
          <EmailIcon {...iconProps} />
        </EmailShareButton>
      </IconRow>
    </Modal>
  );
};
